import axiosLib, { type AxiosInstance } from 'axios'
import {
  type BackendFlagLogListResponse,
  type BackendActionResponse,
  type BackendResponse,
  type BackendFlaggingRule
} from './moderation.api-types'
import { mapFlagLogToMessage } from './moderation.mapper'
import {
  type ModerationFilterParams,
  type PaginatedMessages,
  type FlaggingRule,
  type ApiActionType
} from '../domain/models/moderation.model'
import { type SSEOptions } from '../../../shared/primitives/createSSE'

interface ClubResponse {
  club: { id: number }
}

interface StrapiJwtResponse {
  jwt: string
}

export class ModerationDataSource {
  private clubIdCache = new Map<string, number>()
  private rulesCache: BackendFlaggingRule[] | null = null
  private strapiJwt: string | null = null
  private readonly backendAxios: AxiosInstance

  constructor(
    private readonly baseUrl: string,
    private readonly clerkAxios: AxiosInstance,
    private readonly getOrganizationSlug: () => string | undefined
  ) {
    this.backendAxios = axiosLib.create()
  }

  /**
   * Exchange Clerk JWT for a Strapi JWT via backend-v2.
   * Backend-v2 verifies Strapi JWTs (HS256), not Clerk JWTs (RS256).
   */
  private async getStrapiJwt(): Promise<string> {
    if (this.strapiJwt) return this.strapiJwt
    const response = await this.clerkAxios.post<StrapiJwtResponse>(`${this.baseUrl}/clerk/generate-strapi-jwt`)
    this.strapiJwt = response.data.jwt
    return this.strapiJwt
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const jwt = await this.getStrapiJwt()
    return { Authorization: `Bearer ${jwt}` }
  }

  private async resolveClubId(): Promise<number> {
    const slug = await this.waitForSlug()
    const cached = this.clubIdCache.get(slug)
    if (cached !== undefined) return cached
    const response = await this.backendAxios.get<ClubResponse>(`${this.baseUrl}/clubs/slug/${slug}`)
    const clubId = response.data.club.id
    this.clubIdCache.set(slug, clubId)
    return clubId
  }

  private async waitForSlug(): Promise<string> {
    const slug = this.getOrganizationSlug()
    if (slug) return slug
    await new Promise((r) => setTimeout(r, 100))
    return this.waitForSlug()
  }

  private rulesById = new Map<number, string>()

  private async ensureRulesLoaded(): Promise<void> {
    if (this.rulesCache) return
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<BackendResponse<BackendFlaggingRule[]>>(
      `${this.baseUrl}/clubs/${clubId}/flagging/rules`,
      { headers }
    )
    this.rulesCache = response.data.data
    this.rulesById.clear()
    for (const rule of this.rulesCache) {
      this.rulesById.set(rule.id, rule.name)
    }
  }

  async getRules(): Promise<FlaggingRule[]> {
    await this.ensureRulesLoaded()
    return this.rulesCache!.map((r) => ({ id: r.id, name: r.name }))
  }

  async getLogs(params: ModerationFilterParams): Promise<PaginatedMessages> {
    await this.ensureRulesLoaded()
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])

    const queryParams: Record<string, string> = {
      needs_action: String(params.needsAction),
      page: String(params.page ?? 1),
      pageSize: String(params.pageSize ?? 25)
    }
    if (params.severity) queryParams.severity = params.severity
    if (params.dateFrom) queryParams.date_from = params.dateFrom
    if (params.dateTo) queryParams.date_to = params.dateTo
    if (params.flaggingRuleId) queryParams.flagging_rule_id = String(params.flaggingRuleId)

    const response = await this.backendAxios.get<BackendFlagLogListResponse>(
      `${this.baseUrl}/clubs/${clubId}/moderation/keyword-flagging/logs`,
      { headers, params: queryParams }
    )

    const items = response.data.data.items.map((log) => {
      const ruleName = this.rulesById.get(log.flagging_rule_id) ?? 'Unknown Rule'
      return mapFlagLogToMessage(log, ruleName)
    })

    return { items, pagination: response.data.data.pagination }
  }

  async performAction(logId: string, action: ApiActionType): Promise<void> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    await this.backendAxios.post<BackendActionResponse>(
      `${this.baseUrl}/clubs/${clubId}/moderation/keyword-flagging/logs/${logId}/action`,
      { action },
      { headers }
    )
  }

  async getSSEOptions(onMessage: () => void): Promise<SSEOptions> {
    const [clubId, token] = await Promise.all([this.resolveClubId(), this.getStrapiJwt()])
    return {
      url: `${this.baseUrl}/clubs/${clubId}/moderation/keyword-flagging/events`,
      token,
      onMessage
    }
  }

  clearRulesCache(): void {
    this.rulesCache = null
    this.rulesById.clear()
  }
}
